import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import { SeoActions } from '@store/reducers/seo/actions'
import type { AppState } from '@store/types'
import { selectThread, setActiveTab } from '@storeOld/reducers/canvas/canvas'
import { showComments } from '@storeOld/reducers/comments'
import { openNodeContent } from '@storeOld/reducers/tree'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { useArtboardMode } from '@/hooks/useArtboardMode'
import { GlobalActions } from '@/store'
import {
  deleteSearchParam,
  getSearchParam,
  replaceUrlWithoutRefresh,
  setSearchParam,
} from '@/utils/searchParams'

const ENTITY_BLOCK = 'b'
const ENTITY_NODE = 'p'
const ENTITY_PROJECT = 'pr'
const ENTITY_TAB = 'tab'
const ENTITY_THREAD = 'thread'

const MODAL_NOTE = 'note'
const MODAL_CONTENT = 'content'
const MODAL_COMMENTS = 'comments'
const MODAL_SEO_MODE = 'seo-mode'
const MODAL_ESTIMATE = 'estimate'

type DeepLinkParams = Partial<{
  uuid: string
  tabId: string
  entityType: string
  entityId: string
  modalType: string
}>

/**
 * This hook is designed to replace old legacy modal links with new url structure.
 *
 * Old legacy links, are not more supported:
 *
 * For page comments: https://octopus.do/3qbtcpfsl2r#ltirsltbeh-main-page
 * For block note: https://octopus.do/3qbtcpfsl2r#tfeuydi-header
 *
 * New links (requested in https://linear.app/scada/issue/OCT-1852) examples:
 *
 * Page content: octopus.do/grccr7qbwir/dmlvs8ffj46/p/d23cwll87lh/content
 * Page SEO: octopus.do/grccr7qbwir/dmlvs8ffj46/p/d23cwll87lh/seo
 * Page comments: octopus.do/grccr7qbwir/dmlvs8ffj46/p/d23cwll87lh/comments
 * Block note: octopus.do/grccr7qbwir/dmlvs8ffj46/b/6dhbyiix2o/note
 * Block comments: octopus.do/grccr7qbwir/dmlvs8ffj46/b/6dhbyiix2o/comments
 * Project notes: octopus.do/grccr7qbwir/pr/note
 * Estimates: octopus.do/grccr7qbwir/pr/estimate
 * Tabs: octopus.do/grccr7qbwir/pr/6dhbyiix2o/tabs
 *
 * New link structure:
 * /:uuid/:tabId/:entityType/:entityId/:modalType
 * /:uuid/:entityType/:modalType
 * /:uuid/:entityType/:tabId
 *
 * Each part of the link serves a different purpose:
 *
 * uuid: Project ID
 * entityType: Type of entity being referenced. This can be a block, a page, or potentially other entities in the future, like a sticky note or an external link.
 * entityId: The ID of the specified entity.
 * modalType: The type of modal window that needs to be opened for this entity.
 * tabId: The tab ID; the literal 'null' means the main tab.
 *
 * После открытия модалки url чистится до /:uuid через роутер, params исчезают —
 * поэтому все хуки объявлены безусловно, а гарды живут внутри эффектов
 * (ранний return между хуками ронял рендер: OCT-4807).
 */

export const useShowModalsBySearchParams = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()
  const privateProject = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsPrivate
  )

  const {
    uuid: projectId = null,
    entityType: pathEntityType = null,
    entityId: pathEntityId = null,
    modalType: pathModalType = null,
    tabId: pathTabId = null,
  } = useParams<DeepLinkParams>()

  const stateProjectId = useSelector(migrationProjectSelector.getProjectId)

  const { gallery } = useArtboardMode()

  useEffect(() => {
    if (getSearchParam('images') === 'true') {
      dispatch(artBoardActions.changeMode('gallery'))
    }
  }, [])

  useEffect(() => {
    if (gallery) {
      setSearchParam('images', true)
      return
    }
    deleteSearchParam('images')
  }, [gallery])

  useEffect(() => {
    if (getSearchParam('popupDownloadSitemapXml') === 'true') {
      deleteSearchParam('popupDownloadSitemapXml')
    }
  }, [])

  useEffect(() => {
    const tabId = getSearchParam('tabId') || null

    if (getSearchParam('print')) {
      dispatch(setActiveTab(tabId))
    }
  }, [])

  useEffect(() => {
    if (privateProject || !pathEntityId) {
      return
    }

    dispatch(
      GlobalActions.jumpTo({ selector: 'data-scroll-id', value: pathEntityId })
    )
  }, [pathEntityId])

  useEffect(() => {
    if (privateProject || !stateProjectId) {
      return
    }

    if (!projectId || !pathEntityType || !pathModalType) {
      return
    }

    const activateTab = (tabId: string | null) => {
      if (tabId) {
        dispatch(setActiveTab(tabId === 'null' ? null : tabId))
      }
    }

    const timeout = setTimeout(() => {
      if (pathEntityType === ENTITY_THREAD && pathEntityId) {
        dispatch(selectThread(pathEntityId))
        activateTab(pathTabId)
        dispatch(
          GlobalActions.jumpTo({
            selector: 'data-jump-id',
            value: pathEntityId,
          })
        )
      }

      if (
        pathEntityType === ENTITY_BLOCK &&
        (pathModalType === MODAL_NOTE || pathModalType === MODAL_COMMENTS)
      ) {
        dispatch(showComments(pathEntityId, 'block'))
        activateTab(pathTabId)
      }

      if (pathEntityType === ENTITY_NODE) {
        if (pathModalType === MODAL_COMMENTS) {
          dispatch(showComments(pathEntityId, 'node'))
          activateTab(pathTabId)
        }

        if (pathModalType === MODAL_SEO_MODE && pathEntityId) {
          activateTab(pathTabId)
          dispatch(SeoActions.enableMode())
          dispatch(SeoActions.show(pathEntityId))
        }

        if (pathModalType === MODAL_CONTENT) {
          dispatch(openNodeContent(pathEntityId))
          activateTab(pathTabId)
        }
      }

      if (
        pathEntityType === ENTITY_PROJECT &&
        pathModalType === MODAL_ESTIMATE
      ) {
        dispatch(artBoardActions.changeMode('estimate'))
      }

      if (pathEntityType === ENTITY_TAB) {
        dispatch(setActiveTab(pathModalType))
      }

      replaceUrlWithoutRefresh(`/${projectId}`)
    }, 500)

    return () => clearTimeout(timeout)
  }, [
    pathEntityType,
    pathEntityId,
    pathModalType,
    stateProjectId,
    privateProject,
  ])
}
